import { StaleRecipeWriteError } from '../../../../../services/recipeWriteConflict';
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { primaryText, secondaryText, greyTextColor, borderPrimary, greenColorClass } from "../../../../../styling/stylingHelper";
import { productKey } from "../../../../../utils/reports/shared";
import type { IngredientAlternative } from "../../../../../services/costPrice/costPriceService";
import {
    AlternativeTarget,
    applyAlternativeToTarget,
    reloadSubrecipes,
    scaleAlternativeQuantity,
} from "./alternativesPropagation";

interface ApplyAlternativeToDishesPanelProps {
    /** Name of the PRIMARY product the alternative hangs off (display only). */
    primaryName: string;
    /** Resolver key of that primary — the row matcher on every target. */
    primaryKey: string;
    /** The alternative being propagated. Live: edits made in the list below are
     *  picked up here, and only read when the user confirms. */
    alternative: IngredientAlternative;
    /** The source dish's own per-portion quantity of the primary — the ratio base. */
    sourcePrimaryQuantity: number;
    targets: AlternativeTarget[];
    onDone: () => void;
}

const targetKey = (target: AlternativeTarget) => `${target.kind}-${target.id}`;

/**
 * "You added this alternative here — the same product is in 6 other dishes, apply
 * it there too?". Mirrors the automation review's ApplyToOtherItemsPanel, with one
 * addition: each row shows the target's OWN primary quantity next to the alternative
 * quantity that will be written, and that quantity is editable before applying.
 *
 * Targets that already carry this alternative are listed but locked — there is
 * nothing to apply there, and merging never overwrites an existing entry.
 */
const ApplyAlternativeToDishesPanel: React.FC<ApplyAlternativeToDishesPanelProps> = ({
    primaryName,
    primaryKey,
    alternative,
    sourcePrimaryQuantity,
    targets,
    onDone,
}) => {
    const { t } = useTranslation();

    const alternativeKey = productKey(alternative.platform, alternative.articleId, alternative.productName);
    const applicable = useMemo(
        () => targets.filter(target => !target.existingAlternativeKeys.includes(alternativeKey)),
        [targets, alternativeKey]
    );

    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(applicable.map(targetKey)));
    // Only the rows the user actually typed in. Everything else stays derived, so
    // editing the alternative's own quantity below re-flows into the pre-filled values.
    const [editedQuantities, setEditedQuantities] = useState<Record<string, string>>({});
    const [isApplying, setIsApplying] = useState(false);
    const [appliedCount, setAppliedCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);

    const altUnit = alternative.quantityInfo?.stdUnit ?? '';

    const suggestedQuantity = (target: AlternativeTarget) =>
        scaleAlternativeQuantity(alternative.quantityNeeded, sourcePrimaryQuantity, target.primaryQuantity);

    const quantityValue = (target: AlternativeTarget) => {
        const edited = editedQuantities[targetKey(target)];
        return edited !== undefined ? edited : suggestedQuantity(target).toString();
    };

    const toggle = (key: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const allSelected = applicable.length > 0 && selectedIds.size === applicable.length;

    const handleApply = async () => {
        const selected = applicable.filter(target => selectedIds.has(targetKey(target)));
        if (selected.length === 0) {
            onDone();
            return;
        }

        setIsApplying(true);
        setAppliedCount(0);
        setFailedCount(0);
        let failures = 0;
        let touchedSubrecipe = false;
        // Sequential, like the automation review's bulk apply: each target is a
        // read-modify-write of its own record, so parallelism only buys races.
        for (const target of selected) {
            const parsed = parseFloat(quantityValue(target));
            const quantity = !isNaN(parsed) && parsed > 0 ? parsed : suggestedQuantity(target);
            try {
                await applyAlternativeToTarget(target, primaryKey, alternative, quantity);
                if (target.kind === 'subrecipe') touchedSubrecipe = true;
                setAppliedCount(prev => prev + 1);
            } catch (err) {
                console.error(`Failed to apply alternative to ${target.kind} ${target.id}:`, err);
                if (err instanceof StaleRecipeWriteError) alert(err.message);
                failures += 1;
            }
        }
        // Subrecipes have no per-item store upsert — one reload keeps the modal's
        // subrecipe data in step with what we just wrote.
        if (touchedSubrecipe) {
            try {
                await reloadSubrecipes();
            } catch (err) {
                console.error('Failed to reload subrecipes after propagation:', err);
            }
        }
        setIsApplying(false);

        if (failures > 0) {
            setFailedCount(failures);
            return;
        }
        onDone();
    };

    return (
        <div className="mb-5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 space-y-3">
            <div>
                <p className={`text-[14px] font-semibold ${primaryText}`}>
                    {t('costPrice.alternatives.propagate.title', { product: alternative.productName })}
                </p>
                <p className={`text-[12px] ${greyTextColor} mt-0.5`}>
                    {t('costPrice.alternatives.propagate.subtitle', { count: targets.length, primary: primaryName })}
                </p>
            </div>

            {applicable.length === 0 ? (
                <p className={`text-[13px] ${greyTextColor}`}>
                    {t('costPrice.alternatives.propagate.noneApplicable')}
                </p>
            ) : (
                <button
                    type="button"
                    onClick={() => setSelectedIds(allSelected ? new Set() : new Set(applicable.map(targetKey)))}
                    className={`text-[12px] font-semibold ${secondaryText} hover:opacity-70`}
                >
                    {allSelected
                        ? t('costPrice.alternatives.propagate.deselectAll')
                        : t('costPrice.alternatives.propagate.selectAll')}
                </button>
            )}

            <div className="max-h-64 overflow-y-auto space-y-1.5">
                {targets.map(target => {
                    const key = targetKey(target);
                    const alreadyAdded = target.existingAlternativeKeys.includes(alternativeKey);
                    const isSelected = !alreadyAdded && selectedIds.has(key);
                    return (
                        <div
                            key={key}
                            className={cn(
                                "w-full flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors",
                                alreadyAdded
                                    ? `${borderPrimary} bg-background dark:bg-background-dark opacity-60`
                                    : isSelected
                                        ? "border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-green-900/20"
                                        : `${borderPrimary} bg-background dark:bg-background-dark`
                            )}
                        >
                            <button
                                type="button"
                                disabled={alreadyAdded || isApplying}
                                onClick={() => toggle(key)}
                                className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 disabled:cursor-not-allowed ${
                                    isSelected ? 'bg-darkGreenColor border-darkGreenColor' : 'border-borderGrey dark:border-borderGrey-dark'
                                }`}
                            >
                                {isSelected && <Check className="h-3 w-3 text-white dark:text-white" />}
                            </button>

                            <button
                                type="button"
                                disabled={alreadyAdded || isApplying}
                                onClick={() => toggle(key)}
                                className="min-w-0 flex-1 text-left disabled:cursor-not-allowed"
                            >
                                <span className={`block text-[14px] font-medium ${primaryText} truncate`}>
                                    {target.name}
                                    {target.kind === 'subrecipe' && (
                                        <span className={`ml-2 text-[11px] font-semibold ${greyTextColor}`}>
                                            {t('costPrice.alternatives.propagate.subrecipeBadge')}
                                        </span>
                                    )}
                                </span>
                                <span className={`block text-[12px] ${greyTextColor} truncate`}>
                                    {alreadyAdded
                                        ? t('costPrice.alternatives.propagate.alreadyAdded')
                                        : t('costPrice.alternatives.propagate.currentUsage', {
                                            quantity: target.primaryQuantity,
                                            unit: target.primaryUnit,
                                            product: primaryName,
                                        })}
                                </span>
                            </button>

                            {!alreadyAdded && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className={`text-[12px] ${greyTextColor}`}>→</span>
                                    <input
                                        type="number" inputMode="decimal" min={0} step="any"
                                        disabled={isApplying}
                                        value={quantityValue(target)}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                                setEditedQuantities(prev => ({ ...prev, [key]: value }));
                                            }
                                        }}
                                        className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[14px] leading-5 font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none disabled:opacity-50"
                                    />
                                    <span className={`text-[12px] font-medium ${secondaryText}`}>{altUnit}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {failedCount > 0 && (
                <p className="text-[13px] text-red-600 dark:text-red-400">
                    {t('costPrice.alternatives.propagate.failed', { count: failedCount })}
                </p>
            )}

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onDone}
                    disabled={isApplying}
                    className={`text-[13px] font-medium ${greyTextColor} hover:opacity-70 disabled:opacity-50`}
                >
                    {t('costPrice.alternatives.propagate.skip')}
                </button>
                <button
                    type="button"
                    onClick={handleApply}
                    disabled={isApplying || selectedIds.size === 0}
                    className={`ml-auto h-9 px-4 justify-center text-[14px] font-semibold ${greenColorClass} disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                >
                    {isApplying && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isApplying
                        ? t('costPrice.alternatives.propagate.applying', { done: appliedCount, total: selectedIds.size })
                        : t('costPrice.alternatives.propagate.confirm', { count: selectedIds.size })}
                </button>
            </div>
        </div>
    );
};

export default ApplyAlternativeToDishesPanel;
